import { useGraphStore } from '../../stores/useGraphStore'

export const hasActiveFilters = filters =>
    filters.sentiments.length > 0 ||
    filters.tags.length > 0 ||
    filters.topics.length > 0

export default function filterStatements(prevFilters, filters, statements) {
    if (!hasActiveFilters(filters)) {
        return statements
    }

    const hasNothingChanged =
        JSON.stringify(prevFilters) === JSON.stringify(filters)

    if (hasNothingChanged) return statements

    const hasTopicsChanged = !arraysAreEqual(prevFilters.topics, filters.topics)
    const filteredBySentiment = filterBySentiments(statements, filters)
    const filteredByTags = filterByTags(statements, filters)
    // we want to avoid filtering by topics if topics haven't changed
    // that is because topic names and topic id generated every time anew
    // so we can't ensure the deterministic behavior
    const filteredByTopics = hasTopicsChanged
        ? filterByTopics(statements, filters)
        : []
    const filterResults = [
        filteredBySentiment,
        filteredByTags,
        filteredByTopics,
    ]
    const activeFilters = filterResults.filter(arr => arr.length > 0)
    const filteredStatements = intersectArrays(activeFilters)

    return filteredStatements
}

const arraysAreEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false
    const sortedArr1 = [...arr1].sort()
    const sortedArr2 = [...arr2].sort()
    return sortedArr1.every((value, index) => value === sortedArr2[index])
}

function intersectArrays(arrays) {
    if (arrays.length === 0) return []
    // Start with the first array, then filter down
    return arrays.reduce((acc, curr) => acc.filter(x => curr.includes(x)))
}

export const filterBySentiments = (statements, filters) => {
    if (filters.sentiments.length === 0) return []
    return statements.filter(statement =>
        filters.sentiments.includes(statement.sentiment)
    )
}

export const filterByTags = (statements, filters) => {
    if (filters.tags.length === 0) return []
    return statements.filter(statement => {
        if (filters.overlapMode) {
            return filters.tags.every(tag => statement.tags.includes(tag))
        }
        return filters.tags.some(tag => statement.tags.includes(tag))
    })
}

export const filterByTopics = (statements, filters) => {
    if (filters.topics.length === 0) return []

    const filteredByTopics = statements.filter(statement => {
        const filteredCommunityIds = filters.topics.map(({ id }) => id)
        const communityBelongsToFiltered = filteredCommunityIds.includes(
            statement.topCommunity
        )
        const statementBelongsToHighlightedCommunityOf =
            typeof statement.topOfCommunity === 'number' &&
            filteredCommunityIds.includes(statement.topOfCommunity)
        return (
            communityBelongsToFiltered ||
            statementBelongsToHighlightedCommunityOf
        )
    })
    if (filteredByTopics.length !== 0) return filteredByTopics

    const graphData = useGraphStore.getState().graphData
    if (!graphData) return []

    const topClusters =
        graphData.entriesAndGraphOfContext.graph.graphologyGraph.attributes
            ?.top_clusters || []
    const topStatementIds = topClusters.map(cluster => {
        return cluster.topStatementId
    })
    const topCommunities = topClusters.map(cluster =>
        parseInt(cluster.community)
    )

    const communitiesWithoutStatement = topCommunities.filter(
        community =>
            !statements.some(statement => statement.topCommunity === community)
    )

    const topStatementForCommunitiesWithoutStatement = communitiesWithoutStatement.map(
        community => topStatementIds[community]
    )

    const allComunitiesHaveStatemenets = statements.map(statement => {
        if (
            topStatementForCommunitiesWithoutStatement.includes(statement.uid)
        ) {
            const communityWithoutStatement = topStatementIds.indexOf(
                statement.uid
            )
            return {
                ...statement,
                topCommunity: communityWithoutStatement,
            }
        }
        return statement
    })

    return allComunitiesHaveStatemenets.filter(statement => {
        const filteredCommunityIds = filters.topics.map(({ id }) => id)
        return filteredCommunityIds.includes(statement.topCommunity)
    })
}

